import {
  Component,
  ChangeDetectionStrategy,
  Input,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  OnInit,
  Inject,
  Output,
  EventEmitter,
  PLATFORM_ID,
} from '@angular/core';
import { SwiperComponent } from 'swiper/angular';
// import Swiper core and required modules
import SwiperCore, { EffectCoverflow, Pagination } from 'swiper';

import { switchMap, takeUntil, takeWhile } from 'rxjs/operators';
import { interval, Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { Direction } from '@angular/cdk/bidi';
import { DOCUMENT, isPlatformServer } from '@angular/common';
import { ClientConfigService, ImgQuery, Screen, ScreenService } from '../../services';
import { MarketingTrayLayout } from '../../../common';
import { MarketingLandingGtmTrayData } from '../../interfaces/marketing-landing-gtm-tray-data.interface';
import { GoogleTagManagerEventAction } from '../../enums';
import { MarketingTrendingViewModel } from './marketing-trending.view-model';

// install Swiper modules
SwiperCore.use([EffectCoverflow, Pagination]);

// eslint-disable-next-line @typescript-eslint/no-magic-numbers
const TRENDING_AUTO_PLAY_INTERVAL = 2 * 1000; // ms
// eslint-disable-next-line @typescript-eslint/no-magic-numbers
const AUTO_PLAY_HOLD = 5 * 1000; // ms

@Component({
  selector: 'osn-shared-marketing-trending',
  templateUrl: './marketing-trending.component.html',
  styleUrls: ['./marketing-trending.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MarketingTrendingComponent implements AfterViewInit, OnDestroy, OnInit {
  @Input() public vm: MarketingTrendingViewModel;
  @Output() public measureTrendingTrayButtonClick: EventEmitter<MarketingLandingGtmTrayData> = new EventEmitter();

  public isMobileScreen$ = this.screenService.isXS$;
  public imgQuery: ImgQuery;

  private readonly onDestroy$ = new Subject<void>();
  private readonly autoplayInterval$ = new Subject<void>();
  private timer: ReturnType<typeof setTimeout>;
  private autoPlayInterrupted = false;

  @ViewChild('desktopSwiper') public desktopSwiper: SwiperComponent;
  @ViewChild('mobileSwiper') public mobileSwiper: SwiperComponent;

  public get buttonText(): string {
    return this.vm?.button?.labelLocalized[this.translateService.currentLang];
  }

  public get reversedLayout(): boolean {
    return this.vm.layout === MarketingTrayLayout.RightAligned;
  }

  // needed for swiperjs alignment
  get swiperDirection(): Direction {
    if (this.document.dir === 'ltr') {
      return this.vm.layout === MarketingTrayLayout.LeftAligned ? 'ltr' : 'rtl';
    } else {
      return this.vm.layout === MarketingTrayLayout.LeftAligned ? 'rtl' : 'ltr';
    }
  }

  constructor(
    private readonly screenService: ScreenService,
    private readonly translateService: TranslateService,
    private readonly clientConfigService: ClientConfigService,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) private readonly platformId
  ) {}

  public ngOnInit(): void {
    this.imgQuery = {
      XS: 140,
      SM: 260,
      MD: 150,
      LG: 196,
      XL: 281,
    };
  }

  public ngAfterViewInit() {
    this.screenService.changed$.pipe(takeUntil(this.onDestroy$)).subscribe((screen) => {
      if (![Screen.XS, Screen.SM].includes(screen)) {
        return this.desktopSwiper.swiperRef.disable();
      }
      this.desktopSwiper.swiperRef.enable();
    });
    this.startAutoPlay();
  }

  public onNextButtonClick() {
    this.interruptAutoPlay();
    this.swipe('next');
  }

  public onPrevButtonClick() {
    this.interruptAutoPlay();
    this.swipe('prev');
  }

  public onTrendingTrayButtonClick(): void {
    this.measureTrendingTrayButtonClick.emit({
      buttonLabel: this.vm.button.labelLocalized.en,
      type: GoogleTagManagerEventAction.BodyCtaClick,
      trayIndex: this.vm.trayIndex,
    });
  }

  private swipe(dir: 'next' | 'prev') {
    this.desktopSwiper.swiperRef?.enable();
    this.mobileSwiper.swiperRef?.enable();
    if (dir === 'next') {
      this.desktopSwiper.swiperRef?.slideNext();
      this.mobileSwiper.swiperRef?.slideNext();
    } else {
      this.desktopSwiper.swiperRef?.slidePrev();
      this.mobileSwiper.swiperRef?.slidePrev();
    }
    this.desktopSwiper.swiperRef?.disable();
    this.mobileSwiper.swiperRef?.disable();
  }

  private startAutoPlay() {
    if (isPlatformServer(this.platformId)) {
      return;
    }
    this.autoplayInterval$
      .pipe(
        switchMap(() => interval(this.clientConfigService.trendingTraySwipeIntervalInMilliseconds || TRENDING_AUTO_PLAY_INTERVAL)),
        // eslint-disable-next-line rxjs/no-ignored-takewhile-value
        takeWhile(() => !this.autoPlayInterrupted),
        takeUntil(this.onDestroy$)
      )
      .subscribe(() => {
        this.swipe('next');
      });
    this.autoplayInterval$.next();
  }

  private interruptAutoPlay() {
    clearInterval(this.timer);
    this.autoPlayInterrupted = true;
    this.timer = setTimeout(() => {
      this.autoPlayInterrupted = false;
      this.startAutoPlay();
    }, this.clientConfigService.trendingTrayAutoSwipeShouldStartAfterInMilliseconds || AUTO_PLAY_HOLD);
  }

  public ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
